import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { Option, SVGFC } from '@repo/toolkit/types'
import { ChevronUpDownIcon } from '@repo/ui/icons'
import Input, { InputProps } from '@repo/ui/input'
import { useState } from 'react'

import Typography from '../typography'
import { Command, CommandGroup, CommandItem, CommandList } from './phone/command'
import { Popover, PopoverContent, PopoverTrigger } from './phone/popover'

export type UnitBaseInputValue<T extends string | number> = {
  selectedOptionValue?: T
  value?: string
  icon?: SVGFC
}

type Props<T extends string | number> = {
  className?: string
  disabled?: boolean
  readonly?: boolean
  errorMessage?: string
  options?: Option<T>[]
  value?: UnitBaseInputValue<T>
  inputProps?: InputProps
  onChange?: (value: UnitBaseInputValue<T>) => void
}

/**
 * @example
 * ```tsx
 *  <UnitBasedInput
 *    value={value}
 *    onChange={(value) => setValue(value)}
 *    options={Object.entries(currencyEnumMappping).map(([key, label]) => ({
 *      label,
 *      value: key
 *    }))}
 *  />
 * ```
 */
export function UnitBasedInput<T extends string | number>({
  errorMessage,
  className,
  disabled,
  readonly,
  options,
  inputProps,
  value,
  onChange
}: Props<T>) {
  const [open, setOpen] = useState(false)
  const Icon = value?.icon

  return (
    <div>
      <div
        className={cn(
          'border rounded border-grey-stroke h-8 flex items-center gap-1',
          { 'focus-within:border-red border-red': errorMessage },
          className
        )}
      >
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div
              onClick={() => setOpen(true)}
              className={cn('flex rounded-e-none rounded-s-lg pl-1 items-center', {
                'cursor-pointer': !disabled && !readonly,
                'pointer-events-none': disabled || readonly
              })}
            >
              {Icon && <Icon />}
              <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
                {options?.find((option) => option.value === value?.selectedOptionValue)?.label}
              </Typography>
              <ChevronUpDownIcon />
            </div>
          </PopoverTrigger>
          <PopoverContent className={'w-44 p-0 bg-white'}>
            <Command>
              <CommandList>
                <CommandGroup>
                  {options?.map((option) => (
                    <CommandItem
                      key={option.value}
                      className={'hover:bg-grey-light cursor-pointer'}
                      onSelect={() => {
                        onChange?.({ ...value, selectedOptionValue: option.value })
                        setOpen(false)
                      }}
                    >
                      <span className={'flex-1 text-sm'}>{option.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Input
          type={'text'}
          disabled={disabled}
          readOnly={readonly}
          value={value?.value}
          className={'px-0'}
          wrapperClassName={'w-full border-none'}
          onChange={(e) => {
            if (value) onChange?.({ ...value, value: e.target.value })
          }}
          {...inputProps}
        />
      </div>

      {errorMessage && (
        <div>
          <div className="h-1" />
          <Typography variant="small" color={tailwindColors.red.DEFAULT}>
            {errorMessage}
          </Typography>
        </div>
      )}
    </div>
  )
}
